import FocusableNode from '../Tree/FocusableNode';
import ILookupTable from '../Tree/Interfaces/ILookupTable';
import IParentLookupTable from '../Tree/Interfaces/IParentLookupTable';

import Config from './Config';

/**
 * Contains all core mutable state for the focus manager instance.
 */

class FocusManagerState {
    /**
     * Consumer provided configuration.
     */

    public config: Config = new Config();

    /**
     * A mutable object to be injected into the horizontal throttle factory, ensuring any throttled
     * function created has a mutable throttle duration.
     */

    public horizontalThrottleConfig: {delay: number} = {
        delay: 0,
    };

    /**
     * A mutable object to be injected into the vertical throttle factory, ensuring any throttled
     * function created has a mutable throttle duration.
     */

    public verticalThrottleConfig: {delay: number} = {
        delay: 0,
    };

    /**
     * A private reference to the currently focused node or `null` if no node is in focus. Can only
     * be accessed via its associated "getter" and "setter" functions.
     */

    public $$focusedNode: FocusableNode | null = null;

    /**
     * An internal map used to quickly lookup any focusable component by its ID.
     */

    public lookupTable: ILookupTable = {};

    /**
     * An internal map used to temporarily store pending focusable nodes for a particular parent (by
     * ID) as they register, before that parent has mounted and been registered itself.
     */

    public parentLookupTable: IParentLookupTable = {};

    /**
     * A living tree of all focusable nodes within the provider.
     */

    public root: FocusableNode | null = null;

    /**
     * A list of the IDs of all virtual containers which are currently updating. Used to filter
     * virtual focus change requests from virtual containers which aren't changing focus.
     */

    public updatingVirtualContainers: string[] = [];

    /**
     * Stores the ID of a node to be focused, once the root has mounted. Components may request
     * focus on mount, but the full tree is not guaranteed to have mounted and registered at this
     * point, so any IDs are temporarily stored.
     */

    public pendingIdToFocus: string = '';

    /**
     * The index of the focused anchor within the currently focused node, or `0` if no node is in
     * focus.
     */

    public focusedAnchorIndex: number = 0;
}

export default FocusManagerState;
